import { AlertTriangle, Sparkles } from 'lucide-react'
import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils'
import type { FrequencySummaryEntry } from '@/lib/api'

interface QuotaMeterProps {
  selectedIds: Set<string>
  frequency: Map<string, FrequencySummaryEntry>
  planLabel: string
  planLimit: number
  pendingCompute: number
}

const PROJECTION_DAYS = 30

/**
 * Estimates the user's monthly summary load based on the upload frequency of
 * the channels they've selected. Channels with no computed frequency yet are
 * counted as 0 and surfaced as "still estimating".
 */
function projectMonthlyLoad(
  selectedIds: Set<string>,
  frequency: Map<string, FrequencySummaryEntry>
): { projected: number; unknownCount: number } {
  let projected = 0
  let unknownCount = 0
  for (const id of selectedIds) {
    const entry = frequency.get(id)
    if (!entry || entry.frequency_computed_at === null) {
      unknownCount++
      continue
    }
    const count = entry.recent_upload_count ?? 0
    const window = entry.frequency_window_days || PROJECTION_DAYS
    projected += (count / window) * PROJECTION_DAYS
  }
  return { projected: Math.round(projected), unknownCount }
}

export function QuotaMeter({ selectedIds, frequency, planLabel, planLimit, pendingCompute }: QuotaMeterProps) {
  const { projected, unknownCount } = projectMonthlyLoad(selectedIds, frequency)
  const ratio = planLimit > 0 ? projected / planLimit : 0
  const overLimit = projected > planLimit
  const nearLimit = !overLimit && ratio >= 0.8

  const barColor = overLimit
    ? 'bg-destructive'
    : nearLimit
      ? 'bg-amber-500'
      : 'bg-primary'

  return (
    <div className="rounded-xl border bg-card p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" />
          <p className="text-sm font-medium">Estimated monthly summaries</p>
        </div>
        <p className={cn('text-sm font-semibold tabular-nums', overLimit && 'text-destructive')}>
          ~{projected} / {planLimit}
        </p>
      </div>

      <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-muted">
        <div
          className={cn('h-full rounded-full transition-all', barColor)}
          style={{ width: `${Math.min(100, ratio * 100)}%` }}
        />
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs">
        <p className="text-muted-foreground">
          On the <span className="font-medium text-foreground">{planLabel}</span> plan you get{' '}
          <span className="font-medium text-foreground">{planLimit} summaries</span> per billing cycle.
        </p>
        {pendingCompute > 0 && (
          <p className="text-muted-foreground italic">
            Estimating activity for {pendingCompute} channel{pendingCompute === 1 ? '' : 's'}…
          </p>
        )}
      </div>

      {overLimit && (
        <div className="mt-3 flex items-start gap-2 rounded-lg bg-destructive/10 p-2.5 text-xs text-destructive">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <div className="flex-1">
            <p className="font-medium">This selection will exceed your plan.</p>
            <p className="mt-0.5 text-destructive/80">
              We'll only summarize the first {planLimit} videos this cycle. Consider unfollowing
              high-frequency channels or{' '}
              <Link to="/billing" className="font-medium underline">
                upgrading your plan
              </Link>
              .
            </p>
          </div>
        </div>
      )}

      {unknownCount > 0 && projected > 0 && !overLimit && (
        <p className="mt-2 text-xs text-muted-foreground">
          {unknownCount} selected channel{unknownCount === 1 ? '' : 's'} not yet measured. Your real
          total may be higher.
        </p>
      )}
    </div>
  )
}
